import { Texture } from '@babylonjs/core/Materials/Textures/texture';
import type { Scene } from '@babylonjs/core/scene';
import type { ArenaDaylight } from './arenaDaylight';

/**
 * The skies an arena's theme may name (audit step 26b, `client-arena:quality#10`): a panorama
 * wrapped round the duel on a screen, where every arena had the same gradient. CC0 from Poly Haven
 * (see CREDITS.md), 2048 × 1024. A theme without one keeps the gradient.
 */
export const ARENA_SKIES: Readonly<Record<string, string>> = {
  forest: '/textures/arena/sky-forest.webp',
};

/**
 * How bright a theme's panorama shows: as the day's gradient does by day, and by night dim, a
 * little less dark on a square nearer the dawn.
 */
export function themeSkyLevel(
  daylight: Pick<ArenaDaylight, 'night' | 'skyLevel' | 'brightness'>,
): number {
  return daylight.night ? 0.12 + 0.3 * daylight.brightness : daylight.skyLevel;
}

/** Loads each theme's panorama once, as soon as an arena that names it is built. */
export class ArenaSkies {
  private readonly loaded = new Map<string, Texture>();

  constructor(private readonly scene: Scene) {}

  /** The panorama for a theme's sky, or null for a theme that names none the game has. */
  get(sky: string | undefined): Texture | null {
    const url = sky ? ARENA_SKIES[sky] : undefined;
    if (!url) return null;
    let texture = this.loaded.get(url);
    if (!texture) {
      // Laid on the sky dome as the board's night sky is: not flipped.
      texture = new Texture(url, this.scene, false, false);
      this.loaded.set(url, texture);
    }
    return texture;
  }

  dispose(): void {
    for (const texture of this.loaded.values()) texture.dispose();
    this.loaded.clear();
  }
}
